import {useQuery, UseQueryOptions, UseQueryResult} from 'react-query';
import {AxiosError} from 'axios';
import {GlobalNotificationHandler} from 'utils/errorHandler';
import * as signalR from '@microsoft/signalr';
import {queryClient} from 'App/App';
import {StreamScreen, StreamStyle} from 'types/dtos/Screen';

const API_ROOT = process.env.REACT_APP_WS_URL || '';

const connection = new signalR.HubConnectionBuilder()
  .withUrl(API_ROOT, {
    skipNegotiation: true,
    transport: signalR.HttpTransportType.WebSockets,
  })
  .withAutomaticReconnect()
  .build();

const ChangeMedia = async (mediaId?: string) => {
  await queryClient.cancelQueries('media');
  const previousScreens = queryClient.getQueryData<StreamScreen>('media');
  if (previousScreens) {
    queryClient.setQueryData<StreamScreen>('media', {
      ...previousScreens,
      CurrentMedia: mediaId,
    });
  }
};

const ChangeStyle = async (style?: string) => {
  await queryClient.cancelQueries('style');
  queryClient.setQueryData<StreamStyle>('style', {
    CurrentStyle: style,
  });
};

export function useStreamStyle(
  errorHandler?: (error: AxiosError<any>) => void,
  options?: UseQueryOptions<
    StreamStyle | undefined,
    AxiosError,
    StreamStyle | undefined
  >,
): UseQueryResult<StreamStyle | undefined, AxiosError<any>> {
  return useQuery('style', (): StreamStyle | undefined => {
    const data = queryClient.getQueryData<StreamStyle>('style');
    return data;
  });
}

export function useStreamScreenConnector<TData = StreamScreen>(
  uid: string,
  errorHandler?: (error: AxiosError<any>) => void,
  options?: UseQueryOptions<TData, AxiosError, TData>,
): UseQueryResult<TData, AxiosError<any>> {
  return useQuery(
    'media',
    async (): Promise<TData> => {
      await connection.start();
      return new Promise<TData>((res) => {
        connection.on('ScreenConnected', (response: TData): void => {
          res(response);
        });
        connection.on('SetMedia', (mediaId: string) => {
          if (mediaId === 'nothing') {
            queryClient.invalidateQueries('media');
            console.log('NOTHING');
          } else {
            ChangeMedia(mediaId);
          }
        });
        connection.on('SetStyle', (style: string) => {
          ChangeStyle(style);
        });
        connection.onreconnected(() => {
          connection.send('ConnectScreen', uid);
        });
        connection.send('ConnectScreen', uid);
      });
    },
    {
      ...options,
      onError: errorHandler || GlobalNotificationHandler,
      refetchOnWindowFocus: false,
      retryDelay: 1000,
      staleTime: Infinity,
      cacheTime: 0,
      retry: true,
    },
  );
}
